<template>
  <div
    class="custom-attributes--panel"
    :class="{ 'in-modal-form': inModalForm }"
  >
    <custom-field-item
      v-for="attribute in filteredAttributes"
      :key="attribute.id"
      :attribute="attribute"
      :class="attributeClass"
      @edit="onEdit"
      @delete="onDelete"
      @copy="onCopy"
    />
    <p
      v-if="!filteredAttributes.length"
      class="text-sm text-gray-400 text-center mt-5 px-4"
    >
      {{ $t('CUSTOM_ATTRIBUTES.EMPTY_STATE') }}
    </p>

    <custom-field-modal
      :show="showEditModal"
      mode="edit"
      :edit-attribute="editingAttribute"
      @close="showEditModal = false"
      @update-field="onUpdateField"
    />
  </div>
</template>

<script>
import CustomFieldItem from './CustomFieldItem.vue';
import CustomFieldModal from './CustomFieldModal.vue';
import alertMixin from 'shared/mixins/alertMixin';
import attributeMixin from 'dashboard/mixins/attributeMixin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  components: {
    CustomFieldItem,
    CustomFieldModal,
  },
  mixins: [alertMixin, attributeMixin],
  props: {
    attributeType: {
      type: String,
      default: 'conversation_attribute',
    },
    attributeClass: {
      type: String,
      default: '',
    },
    ticketId: { type: Number, default: null },
  },
  data() {
    return {
      showEditModal: false,
      editingAttribute: null,
    };
  },
  computed: {
    inModalForm() {
      return this.attributeType === 'agent_attribute';
    },
    contactId() {
      return this.contact?.id;
    },
  },
  watch: {
    showEditModal(value) {
      if (value) {
        this.updateZindex(20);
      } else {
        setTimeout(() => {
          this.updateZindex(10);
        }, 300);
      }
    },
  },
  methods: {
    onEdit({ attribute }) {
      this.editingAttribute = attribute;
      this.showEditModal = true;
    },
    updateZindex(value) {
      const el = document.querySelector('.conversation-sidebar-wrap');
      el.style.zIndex = value;
    },
    async onUpdateField({ attributeKey, value }) {
      await this.onUpdate(attributeKey, value);
      this.showEditModal = false;
      this.editingAttribute = null;
    },
    async onUpdate(key, value) {
      const updatedAttributes = { ...this.customAttributes, [key]: value };
      try {
        if (this.attributeType === 'conversation_attribute') {
          await this.$store.dispatch('updateCustomAttributes', {
            conversationId: this.conversationId,
            customAttributes: updatedAttributes,
          });
        } else if (this.inModalForm) {
          this.$emit('changeAttributes', updatedAttributes);
          return;
        } else if (this.attributeType === 'ticket_attribute') {
          await this.$store.dispatch('tickets/update', {
            conversationId: this.conversationId,
            ticketId: this.ticketId,
            customAttributes: updatedAttributes,
          });
        } else {
          this.$store.dispatch('contacts/update', {
            id: this.contactId,
            custom_attributes: updatedAttributes,
          });
        }
        this.showAlert(this.$t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR');
        this.showAlert(errorMessage);
      }
    },
    async onDelete(key) {
      try {
        const { [key]: remove, ...updatedAttributes } = this.customAttributes;
        if (this.attributeType === 'conversation_attribute') {
          await this.$store.dispatch('updateCustomAttributes', {
            conversationId: this.conversationId,
            customAttributes: updatedAttributes,
          });
        } else if (this.attributeType === 'agent_attribute') {
          this.$emit('changeAttributes', updatedAttributes);
          return;
        } else if (this.attributeType === 'ticket_attribute') {
          await this.$store.dispatch('tickets/deleteCustomAttributes', {
            conversationId: this.conversationId,
            ticketId: this.ticketId,
            customAttributes: [key],
          });
        } else {
          await this.$store.dispatch('contacts/deleteCustomAttributes', {
            id: this.contactId,
            customAttributes: [key],
          });
        }

        this.showAlert(this.$t('CUSTOM_ATTRIBUTES.FORM.DELETE.SUCCESS'));
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('CUSTOM_ATTRIBUTES.FORM.DELETE.ERROR');
        this.showAlert(errorMessage);
      }
    },
    async onCopy(attributeValue) {
      await copyTextToClipboard(attributeValue);
      this.showAlert(this.$t('CUSTOM_ATTRIBUTES.COPY_SUCCESSFUL'));
    },
  },
};
</script>
<style scoped lang="scss">
.custom-attributes--panel {
  @apply flex flex-col gap-5;
}
</style>
